import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colorMap } from '@/colors';
import { log } from '@/logger/logger.ts';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { DownloadIcon } from '@/UI/Icons/DownloadIcon';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { useSaveRecipe } from '@/hooks/useSaveRecipe.tsx';
import { I18N_KEYS } from '@/language/keys';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { useExportView } from '../../Recipe/FlowContext';
import { downloadNodeAsset } from '../../Recipe/NodeAssetDownloader/downloadUtils';
import { hasEditingPermissions } from '../Utils';
import { ErrorMessage } from '../Shared/ErrorMessage/ErrorMessage';
import type { ExportData } from '@/types/node';
import type { NodeId } from 'web';

const logger = log.getLogger('ExportCore');

interface ExportNodeProps {
  id: NodeId;
  data: ExportData;
}

function ExportNode({ id, data }: ExportNodeProps) {
  const role = useUserWorkflowRole();
  const recipeId = useWorkflowStore((state) => state.recipe?.id);
  const recipeName = useWorkflowStore((state) => state.recipe?.name);

  const { inputNode } = data;
  const exportView = useExportView(id);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const saveRecipe = useSaveRecipe();
  const { t } = useTranslation();

  const handleExport = async () => {
    try {
      setIsProcessing(true);
      setErrorMessage(undefined); // Clear any previous error

      const { fileInfo, error } = await exportView.resolveDownloadableFile(recipeId, saveRecipe);

      if (error || !fileInfo) {
        setErrorMessage(error || 'Failed to resolve file');
        return;
      }

      await downloadNodeAsset({
        url: fileInfo.url,
        blob: fileInfo.blob,
        fileType: fileInfo.type,
        nodeName: data.name || id,
        prefix: recipeName,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export file';
      setErrorMessage(errorMessage);
      logger.error('Error fetching or downloading the file', error);
    } finally {
      setIsProcessing(false);
    }
  };

  function shouldDisable(): boolean {
    if (isProcessing) {
      return true;
    }

    if (!hasEditingPermissions(role, data)) {
      return true;
    }

    if (!inputNode) {
      return true;
    }

    return !exportView.isExportable();
  }

  return (
    <DynamicNode2
      id={id}
      data={data}
      className="export"
      handleColor={colorMap.get(data.color)}
      size="small"
      overrideShowFullNode={true}
      inputHandleYPos="50px"
    >
      <>
        <ButtonContained
          mode="outlined"
          size="small"
          fullWidth
          disabled={shouldDisable()}
          loading={isProcessing}
          endIcon={<DownloadIcon />}
          loadingPosition="end"
          onClick={() => void handleExport()}
          sx={{ mb: 1 }}
        >
          {isProcessing && t(I18N_KEYS.RECIPE_MAIN.NODES.EXPORT.EXPORT_CTA_RENDERING)}
        </ButtonContained>
        <ErrorMessage errorMessage={errorMessage} />
      </>
    </DynamicNode2>
  );
}

export default ExportNode;
